import { useLayoutEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import {
    SIGNUP_PATH,
    PERPAY_PLUS_SOLO_APPLICATION,
} from '@perpay-web/authentication/constants/paths';
import { REFRESHING } from '@perpay-web/authentication/constants/states';
import { authentication } from '@perpay-web/authentication/singleton';
import { setUserVerificationModalOpen } from '@perpay-web/components/actions/userVerificationModal';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import {
    CARD_PARTNER_ONBOARDING_APPLY,
    MARKETPLACE_PARTNER_ONBOARDING_APPLY,
} from '@perpay-web/constants/paths';

const getIsRefreshing = (state) =>
    state.authentication.tokenState === REFRESHING;

const useRedirectIfIncomplete = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [isLoadingRedirect, setIsLoadingRedirect] = useState(true);
    const isAuthenticationRefreshing = useSelector(getIsRefreshing);

    useLayoutEffect(() => {
        if (isAuthenticationRefreshing) {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const postLoginPath = params.get('next') || window.location.pathname;
        params.set('next', postLoginPath);

        const missingRequirements = authentication.getSignupRequirements();
        const showPartnerVerifyModal =
            authentication.getShowPartnerVerifyModal();
        const phoneNumber = authentication.getPhoneNumber();
        const phoComplete = params.get('PHO_complete') || false;
        const isPartnerOnboarded = authentication.getIsPartnerOnboarded();
        if (
            missingRequirements.includes('pho_submit_application') &&
            !phoComplete
        ) {
            navigate(`${CARD_PARTNER_ONBOARDING_APPLY}?${params}`, {
                replace: true,
            });
            return;
        }
        if (
            missingRequirements.includes('mp_pho_submit_application') &&
            !phoComplete
        ) {
            navigate(`${MARKETPLACE_PARTNER_ONBOARDING_APPLY}?${params}`, {
                replace: true,
            });
            return;
        }
        if (
            showPartnerVerifyModal &&
            phoneNumber &&
            location.pathname !== SIGNUP_PATH &&
            location.pathname !== PERPAY_PLUS_SOLO_APPLICATION
        ) {
            dispatch(setUserVerificationModalOpen(true));
        } else if (missingRequirements.length > 0) {
            if (!isPartnerOnboarded || !phoneNumber) {
                navigate(`${SIGNUP_PATH}?${params}`, { replace: true });
                return;
            }
        }

        setIsLoadingRedirect(false);
    }, [dispatch, isAuthenticationRefreshing, location.pathname, navigate]);

    return {
        isLoadingRedirect,
    };
};

export function withRedirectIfIncomplete(Component) {
    return (props) => {
        const { isLoadingRedirect } = useRedirectIfIncomplete();

        if (isLoadingRedirect) {
            return <WarningLoaderCircle />;
        }

        return <Component {...props} />;
    };
}
